// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import {
  motion,
  useScroll,
  useMotionValue,
  useMotionValueEvent,
  useTransform
} from 'framer-motion';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import AboutUs from './components/AboutUs';
import Testimonials from './components/Testimonials';
import Booking from './components/Booking';
import Footer from './components/Footer';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import PaymentStatus from './components/PaymentStatus';

// Define variants for navbar
const navVariants = {
  hidden: { y: '-100%', opacity: 0, transition: { ease: 'easeIn' } },
  visible: { y: 0, opacity: 1, transition: { ease: 'easeOut' } }
};

// Main Home Component with all animations
function Home() {
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const { scrollY, scrollYProgress } = useScroll();
  const prevY = useMotionValue(0);
  const [scrollHeight, setScrollHeight] = useState('400vh');

  // Track scroll direction
  useMotionValueEvent(scrollY, 'change', latest => {
    const isScrollDown = latest > prevY.get();
    setIsScrollingDown(isScrollDown);
    prevY.set(latest);
  });

  // Adjust scroll height based on viewport - for Hero animations
  useEffect(() => {
    const updateScrollHeight = () => {
      const width = window.innerWidth;
      if (width < 640) { // sm breakpoint
        setScrollHeight('200vh'); // More space for animations
      } else if (width < 768) { // md breakpoint
        setScrollHeight('180vh');
      } else {
        setScrollHeight('150vh'); // Enough space for overlay animations
      }
    };

    updateScrollHeight();
    window.addEventListener('resize', updateScrollHeight);
    return () => window.removeEventListener('resize', updateScrollHeight);
  }, []);

  // SCROLL ADJUSTMENT GUIDE:
  // Total animated scroll: 150vh desktop, 180vh tablet, 200vh mobile
  // SEQUENTIAL FLOW - each element completes before next starts
  //
  // - 0-5%: Hero visible, waiting
  // - 5-20%: IMAGE slides up to position (completes first)
  // - 20-40%: ORANGE CIRCLE slides up to position (after image is done)
  // - 40-80%: Both overlays stay visible with Hero
  // - 80-100%: Hero + overlays exit together
  // After 150vh: Services appears and fades in, then normal scrolling
  //
  // Key insight: Sequential animation creates clear visual hierarchy

  // First overlay (IMAGE) - slides up FIRST
  const overlayY1 = useTransform(
    scrollYProgress,
    [0, 0.01, 0.10, 0.80, 1.0],  // Starts immediately at 1%, reaches center quickly at 10%
    ['120vh', '120vh', '0vh', '0vh', '-100vh']
  );

  // Second overlay (ORANGE CIRCLE) - starts AFTER image is in position
  const overlayY2 = useTransform(
    scrollYProgress,
    [0, 0.10, 0.10, 0.25, 0.80, 1.0],  // Waits until image is done (10%), then slides up to center at 25%
    ['120vh', '120vh', '120vh', '0vh', '0vh', '-100vh']
  );

  // Hero section transform - stays until both overlays are in position, then exits
  const heroY = useTransform(
    scrollYProgress,
    [0, 0.80, 1.0],  // Stays until 80%, then exits (AFTER overlays are settled)
    ['0vh', '0vh', '-100vh']
  );

  return (
    <div className="relative">
      {/* Fixed Navbar */}
      <motion.nav
        variants={navVariants}
        initial="visible"
        animate={isScrollingDown ? 'hidden' : 'visible'}
        className="fixed w-full top-0 left-0 z-50"
      >
        <Navbar />
      </motion.nav>

      {/* Hero Section - Fixed initially, then scrolls up after overlays */}
      <motion.div
        className="fixed w-full h-screen z-20 bg-[#e3daec]"
        style={{ y: heroY }}
      >
        <Hero />
      </motion.div>

      {/* First Overlay - slides into view from below */}
      <motion.div
        className="fixed inset-0 flex items-center justify-center pointer-events-none overflow-hidden z-30"
        style={{
          y: overlayY1
        }}
      >
        <div className="w-full h-full flex items-center justify-center relative">
          {/* Container for both image and circle to keep them aligned */}
          <div className="relative">
            {/* OVERLAY IMAGE - Smaller and Centered */}
            <img
              src="ashima-hero.jpg"
              alt="Overlay 1"
              className="object-cover rounded-lg overlay-image 
                  w-[180px] h-[225px]
                  sm:w-[200px] sm:h-[250px]
                  md:w-[220px] md:h-[275px]
                  lg:w-[240px] lg:h-[300px]
                  xl:w-[280px] xl:h-[350px]
                  -translate-y-20
                  sm:-translate-y-16
                  md:-translate-y-12
                  lg:translate-y-0
                  xl:translate-y-0"
            />
          </div>
        </div>
      </motion.div>

      {/* Second Overlay - Orange Circle (cascades after image) */}
      <motion.div
        className="fixed inset-0 flex items-center justify-center pointer-events-none overflow-hidden z-40"
        style={{
          y: overlayY2
        }}
      >
        <div className="w-full h-full flex items-center justify-center relative">
          {/* Container that matches the image container for consistent positioning */}
          <div className="relative">
            {/* Invisible placeholder matching image dimensions to ensure proper alignment */}
            <div className="invisible
                w-[180px] h-[225px]
                sm:w-[200px] sm:h-[250px]
                md:w-[220px] md:h-[275px]
                lg:w-[240px] lg:h-[300px]
                xl:w-[280px] xl:h-[350px]
                -translate-y-20
                sm:-translate-y-16
                md:-translate-y-12
                lg:translate-y-0
                xl:translate-y-0">
            </div>
            {/* ORANGE CIRCLE - Positioned absolutely relative to the image placeholder */}
            <div
              className="absolute pointer-events-none
                  -top-[110px] -right-[40px]
                  sm:-top-[100px] sm:-right-[45px]
                  md:-top-[85px] md:-right-[50px]
                  lg:-top-[55px] lg:-right-[55px]
                  xl:-top-[70px] xl:-right-[70px]"
            >
              {/* SVG Circle with curved text */}
              <svg
                className="w-[90px] h-[90px]
                    sm:w-[100px] sm:h-[100px]
                    md:w-[110px] md:h-[110px]
                    lg:w-[110px] lg:h-[110px]
                    xl:w-[140px] xl:h-[140px]"
                viewBox="0 0 200 200"
              // style={{
              //   filter: 'drop-shadow(0 8px 25px rgba(255, 107, 53, 0.4)) drop-shadow(0 15px 40px rgba(255, 107, 53, 0.2))',
              // }}
              >
                {/* Define gradient */}
                <defs>
                  <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FF8C42" />
                    <stop offset="100%" stopColor="#FF6B35" />
                  </linearGradient>
                  {/* Define the circular path for text - slightly inside the circle */}
                  <path
                    id="textCircle"
                    d="M 100,100 m -65,0 a 65,65 0 1,1 130,0 a 65,65 0 1,1 -130,0"
                    fill="none"
                  />
                </defs>

                {/* The circle background */}
                <circle
                  cx="100"
                  cy="100"
                  r="100"
                  fill="url(#orangeGradient)"
                />

                {/* The curved text - top half */}
                <text
                  fill="#000000"
                  fontSize="20"
                  fontFamily="'Nunito Sans', Arial, sans-serif"
                  fontWeight="600"
                  letterSpacing="4"
                  textAnchor="middle"
                  style={{
                    textTransform: 'uppercase',
                  }}
                >
                  <textPath href="#textCircle" startOffset="45%" className='text-black font-nunito font-light'>
                    NICE TO MEET YOU
                  </textPath>
                </text>
              </svg>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scrollable area for animation triggers - ONLY for Hero animations */}
      <div style={{ height: scrollHeight }}></div>

      {/* Normal Scrolling Sections - Natural flow after Hero animations */}
      <div className="relative w-full z-50" style={{ paddingTop: '140vh' }}>
        <Services />
        <AboutUs />
        <div className="bg-[#e3daed]">
          <Testimonials />
        </div>
        <Booking />
        <Footer />
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/payment/status" element={<PaymentStatus />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;
